import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressIndicator from '@/components/ui/ProgressIndicator';
import StepContent from '@/components/ui/StepContent';
import NavigationControls from '@/components/ui/NavigationControls';
import ResumeUploadStep from './components/ResumeUploadStep';
import ResumeProcessingStep from './components/ResumeProcessingStep';
import ResumeValidationStep from './components/ResumeValidationStep';
import PersonalInfoStep from './components/PersonalInfoStep';
import CareerGoalsStep from './components/CareerGoalsStep';
import JobRolesStep from './components/JobRolesStep';
import ReviewStep from './components/ReviewStep';
import ProcessingVerificationStep from './components/ProcessingVerificationStep';
import SuccessStep from './components/SuccessStep';
import apiRequest from "@/services/requests";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MultiStepOnboarding = () => {
const navigate = useNavigate();
const [countries, setCountries] = useState([]);
const [currentStep, setCurrentStep] = useState(8);
const [resumeFile, setResumeFile] = useState(null);
const [resumeError, setResumeError] = useState(null);
const [processingProgress, setProcessingProgress] = useState(0);
const [isProcessing, setIsProcessing] = useState(false);
const [validationResults, setValidationResults] = useState({
score: 85,
issues: [],
suggestions: []
});
const [formData, setFormData] = useState({
country: '',
city: '',
gender: '',
dob:'',
industry: '',
salaryMin: null,
salaryMax: null,
targetCountry: [],
fluentLanguages:[],
workPreference: '',
jobRoles: [],
});
const [initialFormData, setInitialFormData] = useState(null);
const [errors, setErrors] = useState({});

const steps = [
{ stepId: 1, stepLabel: 'Resume Upload', title: 'Upload Your Resume', description: 'Start by uploading your professional resume' },
{ stepId: 2, stepLabel: 'Processing', title: 'Processing Resume', description: 'Analyzing your resume content' },
// { stepId: 3, stepLabel: 'Validation', title: 'Resume Validation', description: 'Verifying resume quality' },
{ stepId: 3, stepLabel: 'Personal Info', title: 'Personal Information', description: 'Tell us about yourself' },
{ stepId: 4, stepLabel: 'Career Goals', title: 'Career Goals', description: 'Define your career preferences' },
{ stepId: 5, stepLabel: 'Job Roles', title: 'Target Job Roles', description: 'Select your desired positions' },
{ stepId: 6, stepLabel: 'Review', title: 'Review Information', description: 'Verify your details' },
{ stepId: 7, stepLabel: 'Processing', title: 'Setting Up', description: 'Configuring your profile' },
{ stepId: 8, stepLabel: 'Complete', title: 'Success!', description: 'You are all set' }
];





useEffect(() => {
if (currentStep === 2 && processingProgress < 100) {
const timer = setInterval(() => {
setProcessingProgress(prev => {
if (prev >= 100) {
clearInterval(timer);
return 100;
}
return prev + 25;
});
}, 800);
return () => clearInterval(timer);
}
if (currentStep === 3) {
setValidationResults({
score: 85,
issues: [],
suggestions: []
});
}
}, [currentStep, processingProgress]);



useEffect(() => {
// Fetch onboarding data when the component mounts
const fetchOnboardingData = async () => {
let salaryMin = null;
let salaryMax = null;
try {
const data = await apiRequest.getProfile();
if(data){ 

if (data.prof_details.expected_salary_range) {
const [min, max] = data.prof_details.expected_salary_range.split('-');
salaryMin = parseInt(min, 10) || null;
salaryMax = parseInt(max, 10) || null;
}


const dobFormatted = data.personal.dob
? new Date(data.dob).toISOString().split('T')[0]
: '';

const resumeData = data.resume ? {
name: data.resume.filename,
size: data.resume.filesize, 
path: data.resume.filepath,
type:'application/pdf',
saved:true
} : null;

setResumeFile(resumeData);


const formattedData = {
country: data.personal.country || '',
city: data.personal.city || '',
gender: data.personal.gender || '',
dob: dobFormatted || '',
industry: data.prof_details.target_industry || '',
salaryMin,
salaryMax,
targetCountry: safeParseArray(data.prof_details.target_region),
fluentLanguages: safeParseArray(data.prof_details.fluent_languages),
workPreference: data.prof_details.work_preference || '',
jobRoles: safeParseArray(data.prof_details.target_job_roles),
};

// console.log(data.onboarding_step)
setFormData(formattedData);
setInitialFormData(formattedData);
setCurrentStep(data.account.onboarding_step);

}
} catch (error) {

toast.error('An unexpected error occurred, could not fetch data from server', {
position: "bottom-right",
autoClose: 5000,
hideProgressBar: false,
closeOnClick: true,
pauseOnHover: true,
draggable: true,
progress: undefined,
});

}
};

fetchOnboardingData();
callCountries();
}, []);




const callCountries = async() =>{
try{
const response = await apiRequest.countries();
setCountries(response);
}catch(error){
console.error('error fetching countries', error);
}
}


const safeParseArray = (value) => {
if (Array.isArray(value)) return value;

if (typeof value === 'string') {
try {
const parsed = JSON.parse(value);
return Array.isArray(parsed) ? parsed : [];
} catch {
return [];
}
}

return [];
};

const handleFileSelect = (file, error) => {
setResumeFile(file);
setResumeError(error);
};

const validateStep = (step) => {
const newErrors = {};

switch (step) {
case 1:
if (!resumeFile) {
newErrors.resume = 'Please upload your resume';
setResumeError('Please upload your resume');
return false;
}
break;

case 3:
if (!formData?.gender) newErrors.gender = 'Gender is required';
if (!formData?.dob) newErrors.dob = 'Date of birth is required';
if (!formData?.country) newErrors.country = 'Country is required';
if (!formData?.city?.trim()) newErrors.city = 'City is required';
break;

case 4:
if (!formData?.industry) newErrors.industry = 'Industry is required';
if (!formData?.salaryMin || !formData?.salaryMax) newErrors.salary = 'Salary range is required';
if (!formData?.targetCountry || formData?.targetCountry?.length === 0) {
newErrors.targetCountry = 'Target location is required';
}
if (!formData?.workPreference) newErrors.workPreference = 'Work preference is required';
if (!formData?.fluentLanguages) newErrors.fluentLanguages = 'Fluent Language is required';
break;

case 5:
if (!formData?.jobRoles || formData?.jobRoles?.length === 0) {
newErrors.jobRoles = 'Please select at least one job role';
}
break;

default:
break;
}

setErrors(newErrors);
return Object.keys(newErrors)?.length === 0;
};

const handleNext = () => {
if (!validateStep(currentStep)) {
return;
}

// Step 1 - Upload the resume;
if(currentStep === 1){
if(resumeFile?.saved){
setCurrentStep(currentStep + 2)
}else{
uploadResume() 
}
};


// Step 2 - Processing 
if (currentStep === 2 && processingProgress === 100) {
setCurrentStep(currentStep + 1)
return;
};


// Step 3 - Personal info
if(currentStep === 3){

const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
if (!hasChanged) {
setCurrentStep(currentStep + 1);
return;
}

updateProfile({
country:formData.country,
city:formData.city,
address:formData.address,
gender:formData.gender,
dob:formData.dob,
onboarding_step:4
})

};


// Step 4  - Career Goals
if(currentStep === 4){

const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
if (!hasChanged) {
setCurrentStep(currentStep + 1);
return;
}


updateProfile({
target_industry:formData.industry,
expected_salary_range:formData.salaryMin+'-'+formData.salaryMax,
target_region:formData.targetCountry,
work_preference:formData.workPreference,
target_job_roles:formData.jobRoles,
fluent_languages:formData.fluentLanguages,
onboarding_step:5
})
};


// Step 5  - Target Job Roles
if(currentStep === 5){

const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
if (!hasChanged) {
setCurrentStep(currentStep + 1);
return;
}

updateProfile({
target_job_roles:formData.jobRoles,
onboarding_step:6
})
}


if (currentStep === 6) {
setIsProcessing(true);
setCurrentStep(currentStep + 1); // Go to step 7

setTimeout(() => {
setIsProcessing(false);
setCurrentStep(currentStep + 2); // Go to step 8 after 3 seconds
}, 6000);

return;
}


};

const handlePrevious = () => {
if (currentStep > 1) {
setCurrentStep(currentStep - 1);
}
};


const uploadResume = async() =>{
try{
setIsProcessing(true);
setProcessingProgress(0);
const response = await apiRequest.uploadResume(resumeFile);

setCurrentStep(currentStep + 1);
const intervalId = setInterval(() => {
setProcessingProgress(progress => {
if (progress >= 100) {
clearInterval(intervalId);
setIsProcessing(false);
return 100;
}
return progress + 10;
});
}, 500);
}catch(error){
setIsProcessing(false);
if(error.status === 400){
setResumeError(error.data.message);
return;
}

setResumeError('Failed to upload resume! An error occured on the server, please refresh the page and try again');
}
}


const updateProfile = async(data) =>{
try{
setIsProcessing(true);
const response = await apiRequest.updateProfile(data);
setCurrentStep(currentStep + 1);
setIsProcessing(false);
}catch(error){
setIsProcessing(false);
toast.error('Server error, please refresh the page and try again', {
position: "bottom-right",
autoClose: 5000,
hideProgressBar: false,
closeOnClick: true,
pauseOnHover: true,
draggable: true,
progress: undefined,
});


}
}




const handleStartApplying = () => {
updateProfile({onboarding_step:8});
navigate('/');
};


const isNextDisabled = () => {
if (currentStep === 1) return !resumeFile;
if (currentStep === 2) return processingProgress < 100;
return false;
};

const renderStepContent = () => {
switch (currentStep) {

case 1:
return (
<ResumeUploadStep
onFileSelect={handleFileSelect}
selectedFile={resumeFile}
error={resumeError}
/>
);

case 2:
return <ResumeProcessingStep progress={processingProgress} />;

case 3:
return (
<PersonalInfoStep
formData={formData}
onChange={setFormData}
errors={errors}
countries={countries}
/>
);

case 4:
return (
<CareerGoalsStep
formData={formData}
onChange={setFormData}
errors={errors}
countries={countries}
/>
);

case 5:
return (
<JobRolesStep
formData={formData}
onChange={setFormData}
errors={errors}
/>
);

case 6:
return (
<ReviewStep
formData={formData}
resumeFile={resumeFile}
/>
);

case 7:
return <ProcessingVerificationStep />;

case 8:
return <SuccessStep onStartApplying={handleStartApplying} />;

default:
return null;
}
};

const userStep = (currentStep) => {
if (currentStep <= 2) return 1;
if (currentStep === 3) return 2;
if (currentStep <= 7) return 3;
return 4;
};

return (
<div className="min-h-screen bg-background">
<ProgressIndicator
currentStep={userStep(currentStep)}
totalSteps={4}
steps={[
{ stepId: 1, stepLabel: 'Resume Upload', title: 'Upload Your Resume', description: 'Start by uploading your professional resume' },
{ stepId: 2, stepLabel: 'Personal Info', title: 'Personal Information', description: 'Tell us about yourself' },
{ stepId: 3, stepLabel: 'Career Goals', title: 'Career Goals', description: 'Define your career preferences' },
{ stepId: 4, stepLabel: 'Complete', title: 'Success!', description: 'You are all set' }
]}
/>
<StepContent step={steps[currentStep - 1]}>
{renderStepContent()}
</StepContent>
{currentStep <= 6 && (
<NavigationControls
currentStep={userStep(currentStep)}
overalStep={currentStep}
totalSteps={4}
onPrevious={handlePrevious}
onNext={handleNext}
isNextDisabled={isNextDisabled()}
isProcessing={isProcessing}
showPrevious={currentStep > 1}
/>
)}
</div>
);
};

export default MultiStepOnboarding;